import React, { useState } from "react";
import { X, Upload } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const MONTHS = [
  "ene.",
  "feb.",
  "mar.",
  "abr.",
  "may.",
  "jun.",
  "jul.",
  "ago.",
  "sep.",
  "oct.",
  "nov.",
  "dic.",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => currentYear - i);

const LOCATIONS = [
  "Bogotá, Colombia",
  "Medellín, Colombia",
  "Cali, Colombia",
  "Barranquilla, Colombia",
  "Cartagena, Colombia",
  "Buenos Aires, Argentina",
  "Ciudad de México, México",
  "Lima, Perú",
  "Santiago, Chile",
  "Quito, Ecuador",
  "Madrid, España",
  "Barcelona, España",
  "São Paulo, Brasil",
  "Miami, Estados Unidos",
  "Otra",
];

/* ─── Reusable dropdown ─── */
const SelectDropdown = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-900"
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-gray-700"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AddAchievementModal = ({
  isOpen,
  onClose,
  onAdd,
  isEditing,
  initial,
}) => {
  const [achievement, setAchievement] = useState(initial?.achievement || "");
  const [club, setClub] = useState(initial?.club || "");
  const [month, setMonth] = useState(initial?.month || "");
  const [year, setYear] = useState(initial?.year || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [mediaFile, setMediaFile] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    setError("");
    if (!achievement || !club || !month || !year || !location) {
      setError("Todos los campos con * son obligatorios.");
      return;
    }
    onAdd({
      achievement,
      club,
      month,
      year,
      location,
      mediaFile,
    });
    // reset
    setAchievement("");
    setClub("");
    setMonth("");
    setYear("");
    setLocation("");
    setMediaFile(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-8 relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X size={24} />
            </button>

            <h2 className="text-xl font-extrabold text-gray-900 uppercase tracking-wider mb-1">
              Añadir experiencia
            </h2>
            <p className="text-xs text-gray-500 mb-6">
              * El asterisco indica que es obligatorio.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Logro destacado */}
              <input
                type="text"
                placeholder="Logro destacado*"
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900"
              />

              {/* Club */}
              <input
                type="text"
                placeholder="Club/Equipo con el que lo realizó*"
                value={club}
                onChange={(e) => setClub(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900"
              />

              {/* Fecha */}
              <div>
                <p className="text-sm text-gray-700 mb-2 font-medium">
                  Fecha del logro conseguido*
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <SelectDropdown
                    value={month}
                    onChange={setMonth}
                    options={MONTHS}
                    placeholder="Mes"
                  />
                  <SelectDropdown
                    value={year}
                    onChange={setYear}
                    options={YEARS.map(String)}
                    placeholder="Año"
                  />
                </div>
              </div>

              {/* Ubicación */}
              <SelectDropdown
                value={location}
                onChange={setLocation}
                options={LOCATIONS}
                placeholder="Ubicación*"
              />

              {/* Multimedia */}
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Contenido multimedia"
                  value={mediaFile ? mediaFile.name : ""}
                  readOnly
                  className="flex-1 px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400"
                />
                <label className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.15em] uppercase px-5 py-3.5 rounded-lg cursor-pointer transition-colors flex items-center gap-2">
                  Upload
                  <Upload size={16} />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,video/*,.pdf"
                    onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-6">
              <button
                onClick={handleSubmit}
                className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
              >
                Agregar
                <span className="text-base">✦</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddAchievementModal;
